import { RAW_CLASSES_250 } from './classes-250';
import { RAW_CLASSES, type RawClassNode } from './data.generated';
import { ALL_KITS as KITS } from './kits';
import { DEFAULT_BODY, type BodyProfile, type WeaponDef } from './weapons';

export type ClassStatus = 'implemented' | 'planned';

export interface ClassNode extends RawClassNode {
  /** Index réseau stable (ordre du dossier, BASE = 0). */
  index: number;
  status: ClassStatus;
  weapon: WeaponDef;
  body: BodyProfile;
  /** Classes du niveau 250 : toutes les classes d'où l'on peut y évoluer (sinon le seul parent). */
  parents?: string[];
}

const BASE_RAW: RawClassNode = {
  id: 'BASE',
  family: 'Base',
  name: 'Petit tank',
  parent: '',
  level: 1,
  desc: 'tank de départ, un canon',
};

// Les 30 classes du niveau 250 viennent après les 150 du dossier : les index réseau existants ne changent pas.
export const CLASSES: ClassNode[] = [BASE_RAW, ...RAW_CLASSES, ...RAW_CLASSES_250].map((raw, index) => {
  const kit = KITS[raw.id];
  return {
    ...raw,
    index,
    status: kit ? 'implemented' : 'planned',
    weapon: (kit ?? KITS.BASE).weapon,
    body: { ...DEFAULT_BODY, ...(kit?.body ?? {}) },
  };
});

const BY_ID = new Map(CLASSES.map((c) => [c.id, c]));
const CHILDREN = new Map<string, ClassNode[]>();
for (const c of CLASSES) {
  for (const parent of c.parents ?? (c.parent ? [c.parent] : [])) {
    const list = CHILDREN.get(parent) ?? [];
    list.push(c);
    CHILDREN.set(parent, list);
  }
}

/** Classes du dossier (150, niveaux 10 à 60) ; les 30 du niveau 250 sont à part. */
export const DOSSIER_CLASS_COUNT = RAW_CLASSES.length;

export function getClass(id: string): ClassNode {
  const c = BY_ID.get(id);
  if (!c) throw new Error(`Classe inconnue : ${id}`);
  return c;
}

export function classByIndex(i: number): ClassNode {
  return CLASSES[i] ?? CLASSES[0];
}

export function childrenOf(id: string): ClassNode[] {
  return CHILDREN.get(id) ?? [];
}

/**
 * Évolutions proposées au niveau donné.
 * Les enfants d'un nœud sont exclusifs : choisir A1 (niv. 50) termine la branche, A2 (niv. 60) exige d'attendre.
 * Seules les classes implémentées sont sélectionnables ; les autres sont visibles dans l'arbre comme « prévues ».
 */
export function availableEvolutions(classId: string, level: number): ClassNode[] {
  return childrenOf(classId).filter((c) => c.level <= level && c.status === 'implemented');
}

export function upcomingEvolutions(classId: string): ClassNode[] {
  return childrenOf(classId);
}

/**
 * Prochaine évolution vers une classe visée (classe préférée choisie dans l'arbre) : l'enfant de la classe actuelle
 * qui est la cible ou l'un de ses ancêtres. Gère les classes du niveau 250, accessibles depuis plusieurs terminales.
 */
export function nextTowards(currentId: string, targetId: string): ClassNode | undefined {
  return childrenOf(currentId).find((c) => c.id === targetId || isAncestor(c.id, targetId));
}

export function isAncestor(ancestorId: string, id: string): boolean {
  let cur = BY_ID.get(id);
  while (cur && cur.parent) {
    if (cur.parent === ancestorId) return true;
    cur = BY_ID.get(cur.parent);
  }
  return false;
}

/** Chemin depuis BASE jusqu'à la classe (incluse). */
export function lineage(id: string): ClassNode[] {
  const path: ClassNode[] = [];
  let cur = BY_ID.get(id);
  while (cur) {
    path.unshift(cur);
    cur = cur.parent ? BY_ID.get(cur.parent) : undefined;
  }
  return path;
}

/** Meilleure classe ancêtre encore légale pour un niveau (utile pour la conservation 75 % en équipes). */
export function bestLegalAncestor(id: string, level: number): ClassNode {
  const path = lineage(id);
  for (let i = path.length - 1; i >= 0; i--) if (path[i].level <= level) return path[i];
  return CLASSES[0];
}
